import type { IsoTimestamp } from './common.js';

// Bulk onboarding — single-token batch connect (Miki-confirmed 2026-06-12).
// One headless OAuth establishes a reusable, profile-agnostic session token;
// the mapping builder pairs each un-onboarded kyck club to a Facebook Page +
// ad account; the batch runs createProfile → selectFacebookPage → connectAds
// (adAccountId-scoped) per club, reusing the one token.

// Master lists the token can see (read-only, fetched once for the builder).
export type MasterPage = {
  id: string;
  name: string;
  username: string | null;
  category: string | null;
  // A location-qualified name / address when Meta returns one for a Location
  // Page (children under a brand parent often come back with only the bare
  // brand `name`). Defensively captured at runtime; null when absent — the id
  // is then the disambiguator.
  location: string | null;
};

export type MasterAdAccount = {
  id: string; // act_…
  name: string;
  currency: string | null;
  status: string | null;
};

// One row of the mapping table — one un-onboarded kyck club. Built server-side
// with fuzzy suggestions, then editable by the AM (or via CSV round-trip).
export type BulkMappingRow = {
  accountId: string; // kyck Account id (lacks a zernioProfileId)
  accountName: string;
  profileName: string; // defaults to accountName, editable
  pageId: string | null;
  pageName: string | null;
  // The matched page's handle (Meta returns the location qualifier here for
  // Location Pages — name is the bare brand). Drives the matcher + the
  // collapsed field's "@handle · id" line. null when unknown.
  pageUsername: string | null;
  pageConfidence: number; // 0..1 — fuzzy match strength
  adAccountId: string | null; // RAW id as kyck holds it (no act_ prefix) — null when none
  adAccountName: string | null;
  adAccountConfidence: number; // 0..1
  // Where the ad account came from: 'account' = pre-filled from the kyck
  // Account.fbAdAccountId (source of truth, confidence 1); 'match' = fuzzy
  // name match against the master list; 'manual' = AM picked/typed; null = none.
  adAccountSource: BulkAdAccountSource;
  needsReview: boolean; // page or ad account not a confident match
  ready: boolean; // page + ad account both resolved (complete) — NOT yet runnable
  // Explicit AM confirmation. A complete row sits in "needs confirmation" until
  // the AM confirms it; only confirmed rows are eligible for the batch. Editing
  // the page or ad account drops it back to unconfirmed.
  confirmed: boolean;
};

// A row is eligible for the batch run only when it's complete AND confirmed.
export function isRunnableRow(row: BulkMappingRow): boolean {
  return !!row.pageId && !!row.adAccountId && row.confirmed;
}

export type BulkAdAccountSource = 'account' | 'match' | 'manual' | null;

// Wire-safe status of the bulk authorization session. NEVER carries the token.
//
// ⚠️ The session is an AGENCY-WIDE SINGLETON — one Meta grant shared by the
// whole team (it authorizes over the agency's client pages/ad accounts, not a
// personal resource). `authorizedBy*` is the kyck user who established it, so
// the UI can say whose authority a batch acts with; `userName` remains the META
// profile's display name, which is a different fact.
export type BulkSessionStatus = {
  active: boolean;
  userName: string | null;
  authorizedByName: string | null;
  authorizedByEmail: string | null;
  establishedAt: IsoTimestamp | null;
  expiresAt: IsoTimestamp | null;
};

// Per-client status during batch execution. The micro-steps map onto the
// onboarding record's own status progression, so the UI derives these by
// polling the onboarding list:
//   queued→(none) creating_profile→created connecting_page→connected
//   scoping_ads→scoped complete→complete.
export type BulkClientStatus =
  | 'queued'
  | 'creating_profile'
  | 'connecting_page'
  | 'scoping_ads'
  | 'complete'
  | 'failed'
  | 'blocked' // PAYMENT_REQUIRED gate hit before this client ran
  | 'skipped'; // already connected — idempotent no-op

export type BulkClientResult = {
  accountId: string;
  accountName: string;
  status: BulkClientStatus;
  onboardingId: string | null;
  error: string | null;
};

// One client's execution input for the batch run. Persisted on the run record
// (server-side only) so the run is self-describing — the server knows the full
// planned set and can resume the not-yet-settled clients across request-held
// chunks (see the inline/chunked execution model in batchService).
export type BulkExecInput = {
  accountId: string;
  accountName: string;
  profileName?: string | null;
  pageId: string;
  pageName?: string | null;
  adAccountId: string; // act_… or raw digits — selectAdAccount normalizes
};

// Live status of the most-recent batch run, polled by the bulk page so the UI
// can show per-client progress and settle the "Onboard" button when the run
// finishes (success OR failure) — the run itself is fire-and-forget.
export type BulkRunStatus = {
  active: boolean; // a run is in progress (started, recent progress, not finished)
  // A started-but-unfinished run whose last progress is older than the
  // staleness threshold — the request-held chunk loop died (instance reclaimed,
  // tab closed) without finishing. Self-heal: the UI reads this as
  // interrupted/resumable instead of a permanent dead "in progress", so it
  // never wedges. Mutually exclusive with `active` and `finished`.
  interrupted: boolean;
  finished: boolean; // the last run completed (or stopped at the gate)
  startedAt: IsoTimestamp | null;
  finishedAt: IsoTimestamp | null;
  // Who started it. The session is shared, so the run is shared state — a
  // second user is refused a competing start and must be told WHOSE run is
  // live. Null on a legacy run record written before this was captured.
  startedByUid: string | null;
  startedByName: string | null;
  total: number; // clients submitted in the run
  results: BulkClientResult[]; // per-client outcomes, appended as each settles
  paymentRequired: { message: string; reason: string; details: Record<string, unknown> | null } | null;
  // A request-held chunk is advancing the run RIGHT NOW (its lease is live).
  // ⚠️ After a 524 the client waits for this to clear instead of calling
  // /continue, which would start a second chunk over the same clients' Zernio
  // writes. REQUIRED so every construction site has to say.
  chunkInFlight: boolean;
};

// Summary returned when a batch run completes (or stops at the gate).
export type BulkRunSummary = {
  results: BulkClientResult[];
  completed: number;
  failed: number;
  skipped: number;
  blocked: number;
  // Set when a PAYMENT_REQUIRED gate stopped the run — the wizard renders the
  // count/limit banner from it.
  paymentRequired: {
    message: string;
    reason: string;
    details: Record<string, unknown> | null;
  } | null;
};
